import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import portfolioService, { IsinLookupResult } from '../services/portfolio';

export const useIsinLookup = (portfolioId: number) => {
  const queryClient = useQueryClient();

  const [showModal, setShowModal]       = useState(false);
  const [isin, setIsin]                 = useState('');
  const [resolved, setResolved]         = useState<IsinLookupResult | null>(null);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [shares, setShares]                   = useState('');
  const [purchasePrice, setPurchasePrice]     = useState('');
  const [purchaseDate, setPurchaseDate]       = useState('');
  const [purchaseCurrency, setPurchaseCurrency] = useState('EUR');

  const resolveMutation = useMutation({
    mutationFn: (value: string) => portfolioService.resolveIsin(value),
    onSuccess: (data) => {
      setResolved(data);
      setResolveError(null);
    },
    onError: (err: any) => {
      setResolveError(err.response?.data?.error ?? 'Could not resolve ISIN');
      setResolved(null);
    },
  });

  const addMutation = useMutation<unknown, Error, void>({
    mutationFn: () => {
      if (!resolved) throw new Error('Nothing resolved');
      const sharesNum = parseFloat(shares);
      const priceNum  = purchasePrice ? parseFloat(purchasePrice) : undefined;
      const dateVal   = purchaseDate || undefined;

      if (resolved.type === 'etf') {
        return portfolioService.addPortfolioEntry(portfolioId, {
          etf_id: resolved.id,
          shares: sharesNum,
          purchase_price: priceNum,
          purchase_date: dateVal,
          purchase_currency: purchaseCurrency,
        });
      }
      return portfolioService.addPortfolioAssetEntry(portfolioId, {
        asset_id: resolved.id,
        shares: sharesNum,
        purchase_price: priceNum,
        purchase_date: dateVal,
        purchase_currency: purchaseCurrency,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio', portfolioId] });
      reset();
    },
  });

  const reset = () => {
    setIsin('');
    setResolved(null);
    setResolveError(null);
    setShares('');
    setPurchasePrice('');
    setPurchaseDate('');
    setPurchaseCurrency('EUR');
    setShowModal(false);
    resolveMutation.reset();
    addMutation.reset();
  };

  const handleResolve = () => {
    const trimmed = isin.trim().toUpperCase();
    if (trimmed) resolveMutation.mutate(trimmed);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    addMutation.mutate();
  };

  const clearResolved = () => {
    setResolved(null);
    setResolveError(null);
    resolveMutation.reset();
    addMutation.reset();
  };

  return {
    showModal, setShowModal,
    isin, setIsin,
    resolved,
    resolveError,
    shares, setShares,
    purchasePrice, setPurchasePrice,
    purchaseDate, setPurchaseDate,
    purchaseCurrency, setPurchaseCurrency,
    handleResolve,
    handleSubmit,
    clearResolved,
    isResolving: resolveMutation.isPending,
    isSubmitting: addMutation.isPending,
    addError: addMutation.error as any,
    reset,
  };
};
